import { Button, Heading, Input, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type ProcedureType, type Stored } from "@overmap-ai/common"
import { selectProcedureTypesOfOrganization } from "@overmap-ai/core"
import { useTextFilter } from "@overmap-ai/hooks"
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { useProcedureTypeSettingsTable, useProcedureTypeSettingsTableColumns } from "../../components/Tables"
import { Table } from "../../components/Tables/Table"
import { procedureTypeFilterCallback } from "../../filtering/utils"
import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/procedure-types/")

export const ProcedureTypeSettingsView = memo(() => {
	const { organizationId } = routeApi.useParams()

	const procedureTypes = useShallowEqaulSelector(selectProcedureTypesOfOrganization(organizationId))
	const navigate = useNavigate()

	const handleRowClick = useCallback(
		(procedureType: Stored<ProcedureType>) => {
			void navigate({
				from: "/$organizationId/settings/procedure-types/",
				to: "/$organizationId/settings/procedure-types/$procedureTypeId",
				params: (prev) => ({
					...prev,
					procedureTypeId: procedureType.uuid,
				}),
			})
		},
		[navigate],
	)

	const [filteredProcedureTypes, filterValue, setFilterValue] = useTextFilter(
		procedureTypes,
		procedureTypeFilterCallback,
	)

	const columns = useProcedureTypeSettingsTableColumns()

	const table = useProcedureTypeSettingsTable({
		data: filteredProcedureTypes,
		columns,
		enableHiding: false,
	})

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">Procedure types</Heading>
					<Text size="sm" accentColor="base">
						Manage procedure types in your organization.
					</Text>
				</div>

				<Separator size="full" />

				<div className="flex min-h-0 grow flex-col gap-2">
					<div className="flex w-full items-center justify-between gap-2">
						<Input.Root variant="outline">
							<Input.Slot>
								<LuIcon icon={modelIconMapping.search} />
							</Input.Slot>
							<Input.Field
								placeholder="Search"
								value={filterValue}
								onChange={useChangeEventHandler(setFilterValue)}
							/>
						</Input.Root>

						<Link
							from="/$organizationId/settings/procedure-types/"
							to="/$organizationId/settings/procedure-types/create"
						>
							<Button accentColor="primary" variant="surface" size="sm">
								<LuIcon icon={modelIconMapping.add} /> Create
							</Button>
						</Link>
					</div>

					<div className="min-h-0 w-full grow overflow-auto">
						<Table
							className="w-full"
							size="sm"
							variant="surface"
							table={{ ...table }}
							onRowClick={handleRowClick}
							interactive
							emptyText="No procedure types."
						/>
					</div>
				</div>
			</section>
		</ViewContainer>
	)
})
ProcedureTypeSettingsView.displayName = "ProcedureTypeSettingsView"
